import { Amount, InclusionCode, PaxPrice, SuiteNumber, SuitePrice } from "@ssc-booking/api-contracts"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import { t } from "@lingui/macro"
import { I18n } from "@lingui/core"
import { InclusionContent } from "@silversea-ssc/ui-components-react"
import { ReceiptSectionWithLabel } from "../types"
import { SuiteWithPriceAndConfiguration } from "../../../app-core/v3/_common/read-models/suite-category"
import { CardPromoBadgesContent, SanitySuite } from "../../../app-core/_common/sanity/sanity-types"
import { emptyRows, groupPriceSummariesByKey, removeRowsIfAllHaveSameAmount } from "./utils"
import { PromoLabels, summaryForPromotions } from "./promos"
import { airToReceiptSection, isAirIncluded } from "./air"
import { cruiseToReceiptSection } from "./cruise"
import { GenericLabels, genericPromotionsToReceiptSections } from "./generic"
import { summaryForInclusions } from "./infographic"
import { summaryForVSSailingPromotions } from "./vsSales"

export type SelectedFareForReceipt = {
    fareName: string
    inclusionsContent: InclusionContent[]
    inclusions: InclusionCode[]
}

const getPromoLabels = (): PromoLabels => ({
    VS: t`promo-summary-label-VS`,
    VSMembership: t`promo-summary-label-VS-Membership`,
    Generic: t`promo-summary-label-Generic`, //TODO make this label generic again once Discount label will be used
    KidsSaving: t`promo-summary-label-KidsSaving`,
    D2D: t`promo-summary-label-D2D`,
    PriceDowngrade: t`promo-summary-label-PriceDowngrade`,
    ShipboardCredit: t`promo-summary-label-ShipboardCredit`,
    Discount: t`promo-summary-label-Discount`,
})
const getGenericLabels = (): GenericLabels => ({
    unknown: t`generic-summary-label-unknown`,
    hotel: t`generic-summary-label-hotel`,
})

export const buildReceiptSections = (
    i18n_: I18n["_"],
    showInclusions: boolean,
    price: SuitePrice,
    selectedSuite: O.Option<SuiteWithPriceAndConfiguration<SanitySuite>>,
    selectedSuiteNumber: SuiteNumber | undefined,
    promoBadges: readonly CardPromoBadgesContent[],
    selectedFare?: SelectedFareForReceipt,
    selectedGateway?: string,
    vsCode?: string,
): readonly ReceiptSectionWithLabel[] => {
    const { content: sanity } = O.toUndefined(selectedSuite) || { content: { card: { title: t`Current price` } } }
    const airIncluded = isAirIncluded(price.paxPrices)
    const hideAirPromos = airIncluded && showInclusions
    const promoLabels = getPromoLabels()
    const genericLabels = getGenericLabels()

    return [
        ...summaryForCruise(sanity.card.title, price.paxPrices, selectedSuiteNumber),
        ...(selectedFare && showInclusions ? summaryForInclusions(airIncluded, selectedFare, selectedGateway) : []),
        ...(!hideAirPromos ? summaryForFlights(i18n_, price.paxPrices, showInclusions, selectedGateway) : []),
        ...summaryForVSSailingPromotions(price.paxPrices, promoLabels, vsCode),
        ...summaryForPromotions(price.paxPrices, promoLabels, promoBadges),
        ...summaryForGenericDetail(price.paxPrices, genericLabels),
    ]
}

//**** Summary */

const summaryForCruise = (
    suiteName: string,
    paxPrices: readonly PaxPrice[],
    selectedSuiteNumber: SuiteNumber | undefined,
): readonly ReceiptSectionWithLabel[] =>
    F.pipe(
        paxPrices,
        ROA.map(cruiseToReceiptSection(suiteName, selectedSuiteNumber)),
        ROA.match(emptyRows, groupPriceSummariesByKey),
    )

//**** AIR */
const summaryForFlights = (
    i18n_: I18n["_"],
    paxPrices: readonly PaxPrice[],
    hideIfIncluded: boolean,
    selectedGateway?: string,
): readonly ReceiptSectionWithLabel[] =>
    F.pipe(
        paxPrices,
        ROA.map(airToReceiptSection(i18n_, selectedGateway)),
        ROA.compact,
        ROA.match(
            emptyRows,
            F.flow(
                groupPriceSummariesByKey,
                RONEA.map(removeRowsIfAllHaveSameAmount),
                RONEA.filter((x) => !(hideIfIncluded && O.isSome(x.totalAmount) && Amount.isZero(x.totalAmount.value))),
                O.getOrElse(emptyRows),
            ),
        ),
    )

//**** GENERIC PRICE DETAILS */

const summaryForGenericDetail = (
    paxPrices: readonly PaxPrice[],
    labels: GenericLabels,
): readonly ReceiptSectionWithLabel[] =>
    F.pipe(
        paxPrices,
        ROA.chain(genericPromotionsToReceiptSections(labels)),
        RONEA.fromReadonlyArray,
        O.foldW(
            emptyRows,
            F.flow(
                //keep new line
                groupPriceSummariesByKey,
                RONEA.map(removeRowsIfAllHaveSameAmount),
            ),
        ),
    )
